import React, { useCallback, useEffect, useState } from "react";
import { Smartphone, Plus, Pencil, Trash2, Power, Copy, Share2, Check, X, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const PERM_KEYS = ["schedule", "bookings", "customers", "orders", "messages", "notifications"];
const emptyForm = { id: null, name: "", code: "", active: true, badge: "store", perms: { schedule: true, bookings: true, customers: true, orders: true, messages: true, notifications: true } };

// Dev: staff mobile-app users — personal access codes + per-feature permissions
export const AppUsersPanel = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/app-users").then((r) => setUsers(r.data)).catch(() => toast.error("Error"));
  }, []);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.name.trim() || (!form.id && !form.code.trim())) return;
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        perms: form.perms,
        active: form.active,
        badge: form.badge,
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
      };
      if (form.id) await api.put(`/admin/app-users/${form.id}`, payload);
      else await api.post("/admin/app-users", payload);
      toast.success(d.au_saved);
      setForm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/app-users/${u.id}`, { active: !u.active });
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (u) => {
    if (!window.confirm(d.au_delete_confirm)) return;
    try {
      await api.delete(`/admin/app-users/${u.id}`);
      toast.success(d.au_deleted);
      if (form?.id === u.id) setForm(null);
      load();
    } catch { toast.error("Error"); }
  };

  if (users === null) return null;

  return (
    <div className="rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="app-users-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Smartphone className="h-5 w-5 text-brand" /> {d.au_title}</p>
          <p className="text-xs text-brand-ink/50 mt-0.5">{d.au_sub}</p>
        </div>
        {!form && (
          <Button onClick={() => setForm({ ...emptyForm, perms: { ...emptyForm.perms } })} size="sm" className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9 shrink-0" data-testid="app-user-add">
            <Plus className="h-4 w-4 mr-1.5" /> {d.au_add}
          </Button>
        )}
      </div>

      {form && (
        <div className="mt-4 rounded-xl border border-brand/15 bg-stone-alt/40 p-4" data-testid="app-user-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">{d.au_name}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="app-user-name" />
            </div>
            <div>
              <Label className="text-xs">{d.au_code}{form.id ? ` · ${d.au_code_edit_hint}` : ""}</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="app-user-code" />
            </div>
            <div>
              <Label className="text-xs">{d.au_badge}</Label>
              <Select value={form.badge} onValueChange={(v) => setForm({ ...form, badge: v })}>
                <SelectTrigger className="rounded-xl border-brand/20 bg-white h-10 mt-1" data-testid="app-user-badge"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="store">{d.badge_store}</SelectItem>
                  <SelectItem value="management">{d.badge_management}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.badge === "management" ? (
            <p className="mt-4 text-[11px] font-semibold text-brand bg-brand-soft/50 rounded-xl px-3.5 py-2.5 flex items-center gap-2" data-testid="app-user-management-hint">
              <ShieldCheck className="h-4 w-4 shrink-0" /> {d.au_badge_hint}
            </p>
          ) : (
            <>
              <p className="text-xs font-bold text-brand-ink/70 mt-4 mb-2">{d.au_perms}</p>
              <div className="space-y-2.5">
                {PERM_KEYS.map((k) => (
                  <label key={k} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-brand/10 px-3.5 py-2.5" data-testid={`app-user-perm-${k}`}>
                    <span>
                      <span className="block text-sm font-semibold text-brand-ink">{d[`perm_${k}`]}</span>
                      <span className="block text-[11px] text-brand-ink/45 mt-0.5">{d[`perm_desc_${k}`]}</span>
                    </span>
                    <Switch checked={!!form.perms[k]} onCheckedChange={(v) => setForm({ ...form, perms: { ...form.perms, [k]: v } })} />
                  </label>
                ))}
              </div>
            </>
          )}
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={save} disabled={busy || !form.name.trim() || (!form.id && !form.code.trim())} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9" data-testid="app-user-save">
              {d.au_save}
            </Button>
            <button type="button" onClick={() => setForm(null)} className="text-xs font-bold text-brand-ink/50 hover:underline" data-testid="app-user-cancel">✕</button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid="app-users-list">
        {users.length === 0 && !form && <p className="text-sm text-brand-ink/40 italic py-4 text-center">{d.au_empty}</p>}
        {users.map((u) => (
          <div key={u.id} className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${u.active ? "border-brand/10 bg-white" : "border-brand/10 bg-stone-alt/40 opacity-60"}`} data-testid={`app-user-row-${u.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-brand-ink text-sm flex items-center gap-2 flex-wrap">
                {u.name}
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.badge === "management" ? "bg-brand text-brand-cream" : "bg-stone-200 text-stone-600"}`} data-testid={`app-user-badge-${u.id}`}>
                  {u.badge === "management" ? d.badge_management : d.badge_store}
                </span>
                {!u.active && <span className="text-[10px] font-bold uppercase text-brand-ink/40">({d.au_inactive})</span>}
              </p>
              {u.badge === "management" ? (
                <p className="mt-1.5 text-[11px] font-semibold text-brand flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> {d.au_badge_hint}</p>
              ) : (
                <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid={`app-user-perms-${u.id}`}>
                  {PERM_KEYS.map((k) => (
                    <span key={k} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${u.perms?.[k] ? "text-emerald-600" : "text-brand-ink/30"}`}>
                      {u.perms?.[k] ? <Check className="h-3 w-3 shrink-0" /> : <X className="h-3 w-3 shrink-0" />}
                      <span className={u.perms?.[k] ? "" : "line-through"}>{d[`perm_${k}`]}</span>
                      <span className="hidden sm:inline text-[10px] font-normal text-brand-ink/35">— {d[`perm_desc_${k}`]}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button type="button" onClick={() => toggleActive(u)} className="grid place-items-center h-8 w-8 rounded-full hover:bg-brand-soft/60 text-brand" data-testid={`app-user-toggle-${u.id}`} aria-label="active">
                <Power className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => setForm({ id: u.id, name: u.name, code: "", active: u.active, badge: u.badge || "store", perms: { ...emptyForm.perms, ...(u.perms || {}) } })} className="grid place-items-center h-8 w-8 rounded-full hover:bg-brand-soft/60 text-brand" data-testid={`app-user-edit-${u.id}`} aria-label={d.au_edit}>
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => del(u)} className="grid place-items-center h-8 w-8 rounded-full hover:bg-rose-50 text-rose-500" data-testid={`app-user-delete-${u.id}`} aria-label="Delete">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Dev: install instructions for the staff mobile app
export const AppMobilePanel = () => {
  const { t } = useLang();
  const d = t.admin.dev;
  const appUrl = `${window.location.origin}/app`;
  const [pushForm, setPushForm] = useState({ title: "", body: "", user_id: "" });
  const [appUsers, setAppUsers] = useState([]);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/app-users").then((r) => setAppUsers(r.data)).catch(() => {});
  }, []);

  const copy = async () => {
    try { await navigator.clipboard.writeText(appUrl); toast.success(d.am_copied); } catch { window.prompt(appUrl, appUrl); }
  };

  const sendPush = async () => {
    if (!pushForm.title.trim() || !pushForm.body.trim()) return;
    setPushBusy(true);
    try {
      const res = await api.post("/admin/push-broadcast", { title: pushForm.title.trim(), body: pushForm.body.trim(), user_id: pushForm.user_id });
      if (res.data.devices === 0) toast.info(d.am_push_none);
      else toast.success(d.am_push_sent.replace("{n}", `${res.data.sent}/${res.data.devices}`));
      setPushForm({ title: "", body: "", user_id: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="app-mobile-panel">
      <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2"><Smartphone className="h-5 w-5 text-brand" /> {d.am_title}</p>
      <p className="text-xs text-brand-ink/50 mt-0.5">{d.am_sub}</p>

      <div className="mt-4 rounded-xl border border-brand/15 bg-stone-alt/40 p-4">
        <Label className="text-xs font-bold text-brand-ink/70 flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5 text-brand" /> {d.am_link}</Label>
        <div className="mt-2 flex flex-col sm:flex-row gap-2">
          <Input readOnly value={appUrl} className="rounded-xl border-brand/20 bg-white h-10 flex-1 font-mono text-xs" data-testid="app-mobile-url" />
          <Button onClick={copy} className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-10 shrink-0" data-testid="app-mobile-copy">
            <Copy className="h-4 w-4 mr-1.5" /> {t.share.copy}
          </Button>
        </div>
        <p className="mt-1.5 text-[11px] text-brand-ink/40">{d.am_note}</p>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-brand/10 bg-white p-4" data-testid="app-android-card">
          <p className="font-bold text-brand-ink">{d.am_android_title}</p>
          <ol className="mt-2 space-y-1.5 text-sm text-brand-ink/70 list-decimal list-inside">
            <li>{d.am_android_1}</li>
            <li>{d.am_android_2}</li>
            <li>{d.am_android_3}</li>
          </ol>
          <p className="mt-3 text-[11px] text-brand-ink/45 bg-stone-alt/50 rounded-lg px-3 py-2">{d.am_android_apk}</p>
        </div>
        <div className="rounded-xl border border-brand/10 bg-white p-4" data-testid="app-ios-card">
          <p className="font-bold text-brand-ink">{d.am_ios_title}</p>
          <ol className="mt-2 space-y-1.5 text-sm text-brand-ink/70 list-decimal list-inside">
            <li>{d.am_ios_1}</li>
            <li>{d.am_ios_2}</li>
            <li>{d.am_ios_3}</li>
          </ol>
        </div>
      </div>

      {/* Push broadcast to staff devices */}
      <div className="mt-4 rounded-xl border border-brand/15 bg-stone-alt/40 p-4" data-testid="app-push-card">
        <Label className="text-xs font-bold text-brand-ink/70 flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-brand" /> {d.am_push_title}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input value={pushForm.title} onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })} placeholder={d.am_push_t} className="rounded-xl border-brand/20 bg-white h-10" data-testid="app-push-title" />
          <select value={pushForm.user_id} onChange={(e) => setPushForm({ ...pushForm, user_id: e.target.value })} className="rounded-xl border border-brand/20 bg-white h-10 px-3 text-sm" data-testid="app-push-target">
            <option value="">{d.am_push_all}</option>
            {appUsers.filter((u) => u.active && u.perms?.notifications).map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <Input value={pushForm.body} onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })} placeholder={d.am_push_msg} className="mt-2 rounded-xl border-brand/20 bg-white h-10" data-testid="app-push-body" />
        <Button onClick={sendPush} disabled={pushBusy || !pushForm.title.trim() || !pushForm.body.trim()} className="mt-3 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-9" data-testid="app-push-send">
          {d.am_push_send}
        </Button>
      </div>
    </div>
  );
};
